/**
 * Shared layout wrappers used by AppRoutes in App.tsx.
 * Exported here so they can be unit-tested in isolation.
 */
import type React from "react";
import { useEffect } from "react";
import { Link, useLocation } from "wouter";
import logoUrl from "/logo.webp";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingCTABanner from "@/components/FloatingCTABanner";

function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [location]);
  return null;
}

function DemoSourceTracker() {
  useEffect(() => {
    const rememberSource = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const link = target.closest<HTMLAnchorElement>("a[href]");
      if (!link) return;
      const url = new URL(link.href, window.location.href);
      if (!url.pathname.endsWith("/book-demo")) return;
      const label = link.textContent?.trim().replace(/\s+/g, " ") || "Book a Demo";
      try {
        sessionStorage.setItem("demoSubmissionSource", `${window.location.pathname} — ${label}`);
      } catch { /* Storage is optional; the scheduler has a default source. */ }
    };
    document.addEventListener("click", rememberSource);
    return () => document.removeEventListener("click", rememberSource);
  }, []);
  return null;
}

/** Auth routes — no Header, Footer, or FloatingCTABanner.
 *  Renders a full-height solid background so the Clerk card doesn't
 *  float transparently over the marketing page content. */
export function AuthLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-svh w-full min-w-0 bg-background">
      <ScrollToTop />
      {children}
    </div>
  );
}

/** Focused campaign chrome; global MarketingPrivacy remains mounted in App. */
export function CampaignLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-[#181818] text-[#F2EFE9]">
      <ScrollToTop />
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-4">
          <Link href="/" aria-label="Gorilla Works home"><img src={logoUrl} alt="Gorilla Works" className="h-9 sm:h-11" /></Link>
          <span className="text-xs sm:text-sm text-[#b6b0a8]">Direct mail. Built around your business.</span>
        </div>
      </header>
      <main id="main-content" tabIndex={-1}>{children}</main>
      <footer className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 flex flex-wrap justify-between gap-5 text-sm text-[#b6b0a8]">
          <p>© {new Date().getFullYear()} Gorilla Works Marketing</p>
          <nav aria-label="Legal"><div className="flex flex-wrap gap-5">
            <Link href="/privacy">Privacy Policy</Link>
            <Link href="/terms">Terms of Service</Link>
            <Link href="/data-deletion">Data Deletion</Link>
            <button type="button" onClick={() => window.dispatchEvent(new Event("gorilla-privacy-open"))}>
              Your Privacy Choices / Do Not Sell or Share
            </button>
          </div></nav>
        </div>
      </footer>
    </div>
  );
}

/** All non-auth routes — includes Header, Footer, and FloatingCTABanner. */
export function MainLayout({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const hideBanner = location.startsWith("/book-demo") || location.startsWith("/contact")
    || location === "/map-intel" || location === "/map-intel/";
  return (
    <>
      <Header />
      <ScrollToTop />
      <DemoSourceTracker />
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <main id="main-content" tabIndex={-1} className="overflow-x-clip">{children}</main>
      <Footer />
      {!hideBanner && <FloatingCTABanner />}
    </>
  );
}
